import { ScDataValidation } from "interfaces/dataInterfaces"
import { useEffect, useState } from "react"
import { useGetSeriesCombinationQuery } from "services/core/series"

interface Props {
  adm0Code?: number
}

export interface ScDropdowns {
  marketsDropdown: { label: string; value: number }[]
  commoditiesDropdown: { label: string; value: number; marketID: number }[]
  currenciesDropdown: { label: string; value: number; commodityID: number }[]
  priceTypesDropdown: { label: string; value: number; commodityID: number }[]
  unitsDropdown: { label: string; value: number; commodityID: number }[]
}

const useScData = ({ adm0Code }: Props) => {
  const { data } = useGetSeriesCombinationQuery({ admCode: adm0Code || 0, skip: 0, take: 0 })
  const [scValidationData, setScValidationData] = useState<ScDataValidation | undefined>({
    markets: [],
    commodities: [],
    currencies: [],
    priceTypes: [],
    units: [],
  })
  const [scDropdowns, setScDropdowns] = useState<ScDropdowns>({
    marketsDropdown: [],
    commoditiesDropdown: [],
    currenciesDropdown: [],
    priceTypesDropdown: [],
    unitsDropdown: [],
  })

  useEffect(() => {
    if (!data?.items) return
    const markets = [...new Set(data.items.map(({ marketName }) => marketName))]
    const commodities = [...new Set(data.items.map(({ commodityName }) => commodityName))]
    const currencies = [...new Set(data.items.map(({ currencyName }) => currencyName))]
    const priceTypes = [...new Set(data.items.map(({ priceTypeName }) => priceTypeName))]
    const units = [...new Set(data.items.map(({ commodityUnitName }) => commodityUnitName))]

    const marketsDropdown = [
      ...new Set(data.items.map(({ marketName, marketID }) => JSON.stringify({ label: marketName, value: marketID }))),
    ].map(item => JSON.parse(item))
    const commoditiesDropdown = [
      ...new Set(
        data.items.map(({ commodityName, commodityID, marketID }) =>
          JSON.stringify({
            label: commodityName,
            value: commodityID,
            marketID,
          }),
        ),
      ),
    ].map(item => JSON.parse(item))
    const currenciesDropdown = [
      ...new Set(
        data.items.map(({ currencyName, currencyID, commodityID }) =>
          JSON.stringify({
            label: currencyName,
            value: currencyID,
            commodityID,
          }),
        ),
      ),
    ].map(item => JSON.parse(item))
    const priceTypesDropdown = [
      ...new Set(
        data.items.map(({ priceTypeName, priceTypeID, commodityID }) =>
          JSON.stringify({
            label: priceTypeName,
            value: priceTypeID,
            commodityID,
          }),
        ),
      ),
    ].map(item => JSON.parse(item))
    const unitsDropdown = [
      ...new Set(
        data.items.map(({ commodityUnitName, commodityUnitID, commodityID }) =>
          JSON.stringify({
            label: commodityUnitName,
            value: commodityUnitID,
            commodityID,
          }),
        ),
      ),
    ].map(item => JSON.parse(item))

    setScValidationData({ markets, commodities, currencies, priceTypes, units })
    setScDropdowns({ marketsDropdown, commoditiesDropdown, currenciesDropdown, priceTypesDropdown, unitsDropdown })
  }, [data])

  return { data: data?.items, ...scValidationData, ...scDropdowns }
}

export default useScData
